import { formatToDollar, formatToPercent, IndemnityAgreementSignatureMethod } from "@inrev/common";
import {
	deriveIndemnitors,
	useFetchAccountCompanies,
	useFetchAccountIndividuals,
	useFetchAccountLatestIndemnityAgreement,
} from "@portal/api/account";
import { BondRequest } from "@portal/api/bond-request/agent";
import { cn } from "@portal/components/lib/utils";
import { MetricCard } from "@portal/components/patterns/metric-card";
import { Card, ClickableText, Text } from "@portal/components/primitives";
import { useMemo, useState } from "react";
import { carrierLogoIconMap } from "../../../utils/carrier";
import { AccountIndemnitorList } from "../../agent/account/status/submitted/underwritten/indemnity/components/AccountIndemnitorList";
import { FraudWarningsModal } from "./FraudWarningsModal";

type BondRequestTermsAndConditionsProps = {
	request: BondRequest;
	variant?: "header" | "card";
};

export const BondRequestTermsAndConditions = ({
	request,
	variant = "header",
}: BondRequestTermsAndConditionsProps) => {
	const [fraudWarningsOpen, setFraudWarningsOpen] = useState(false);
	const { latestIndemnityAgreement } = useFetchAccountLatestIndemnityAgreement(
		request.suretyAccountId,
	);
	const { companies } = useFetchAccountCompanies(request.suretyAccountId);
	const { individuals } = useFetchAccountIndividuals(request.suretyAccountId);

	const indemnitors = useMemo(
		() =>
			deriveIndemnitors(
				{ companies: companies ?? [], individuals: individuals ?? [] },
				latestIndemnityAgreement,
			),
		[companies, individuals, latestIndemnityAgreement],
	);

	const hasIndemnitors = indemnitors.companies.length > 0 || indemnitors.individuals.length > 0;

	if (request.suretyType !== "contract") return <></>;

	const metricItems =
		request.contractSuretyType === "bid"
			? [
					{
						label: "Final Bond Rate",
						value: formatToPercent(request.finalBondContingentRate, 2, "--"),
					},
					{
						label: "Final Bond Premium",
						value: formatToDollar(request.finalBondContingentPremium, { round: true }) ?? "--",
					},
					{
						label: "Commission",
						value: formatToPercent(request.agencyCommissionPercentage, 2, "--"),
					},
				]
			: [
					{ label: "Rate", value: formatToPercent(request.rate, 2, "--") },
					{
						label: "Premium",
						value: formatToDollar(request.premium, { round: true }) ?? "--",
					},
					{
						label: "Commission",
						value: formatToPercent(request.agencyCommissionPercentage, 2, "--"),
					},
				];

	const body = (
		<div
			className={cn(
				"flex flex-col space-y-[40px] w-full h-fit",
				variant === "header" && "px-[40px] py-[30px] pt-[25px]",
			)}
		>
			<div className="flex flex-col space-y-[20px]">
				<div className="font-medium text-[16px] text-primary">Terms</div>
				<Card elevation="raised">
					<Card.Body className="flex flex-col gap-[14px] px-[20px] py-[14px] pt-[12px]">
						<span className="flex-1 text-[13.5px] text-gray-700 font-[450]">Carrier</span>
						<div className="flex items-center gap-[12px]">
							{request.carrier && carrierLogoIconMap[request.carrier.name]}
							<span className="text-[17px] text-gray-800 font-[475]">
								{request.carrier?.legalName}
							</span>
						</div>
					</Card.Body>
				</Card>
				<div className="flex gap-4">
					{metricItems.map(({ label, value }) => (
						<MetricCard key={label} className="flex-1">
							<MetricCard.Label size="md" className="whitespace-nowrap">
								{label}
							</MetricCard.Label>
							<MetricCard.Value size="md" className="whitespace-nowrap">
								{value}
							</MetricCard.Value>
						</MetricCard>
					))}
				</div>
				{hasIndemnitors && latestIndemnityAgreement?.status !== "complete" && (
					<div className="flex flex-col gap-y-[20px] !mt-[32px]">
						<div className="text-[13px] text-gray-800 font-normal">
							The following individuals and/or companies are required to sign an indemnity
							agreement:
						</div>
						<AccountIndemnitorList
							indemnitors={indemnitors}
							showESignatureDetails={
								latestIndemnityAgreement?.signatureMethod ===
								IndemnityAgreementSignatureMethod.electronic
							}
						/>
					</div>
				)}
			</div>
			<div className="flex flex-col gap-[24px]">
				<Text type="title" color="primary">
					Conditions
				</Text>

				<Text type="caption" color="secondary">
					This approval is contingent upon: (a) the work having not already started (b) the contract
					not involving design responsibility, environmental or remediation work, efficiency
					guarantees, takeover of an existing contract, work at a superfund site or work for a
					sovereign nation owner.
				</Text>
				<Text type="caption" color="secondary">
					I have read and understand the{" "}
					<ClickableText color="link" type="caption" onClick={() => setFraudWarningsOpen(true)}>
						fraud warnings
					</ClickableText>
					.
				</Text>
			</div>
		</div>
	);

	return (
		<div className="flex flex-col w-full min-h-fit">
			{variant === "header" && (
				<div
					className="w-full h-[1.5px] bg-center"
					style={{
						backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='1' viewBox='0 0 10 1' fill='none'%3E%3Cline x1='0' y1='1' x2='10' y2='0' stroke-width='1.5' stroke='%23E5E7EB' stroke-dasharray='4 6'/%3E%3C/svg%3E")`,
					}}
				></div>
			)}
			{body}
			{fraudWarningsOpen && <FraudWarningsModal onClose={() => setFraudWarningsOpen(false)} />}
		</div>
	);
};
